import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { dom, library } from '@fortawesome/fontawesome-svg-core';
import {
  faCalendarClock,
  faHourglassEnd,
  faHourglassHalf,
  faHourglassStart,
  faTools,
} from '@fortawesome/pro-solid-svg-icons';

import { svgWithJs, variables } from '../variables';

// TODO: This component does three things that are sub-optimal:
//  1: It only pulls in the free icons from FontAwesome today
//  2: It loads *all* the icons rather than select ones and registers them with the library (makes the component much larger)
//  3: It loads the needed svg-with-js.css via a constant in variables.ts (this should be loading svg-with-js.min.css straight from the package using the loader somehow)
@customElement('apt-labeled-data-point')
export class LabeledDataPoint extends LitElement {
  @property({ type: String }) color = ``;
  @property({ type: String }) icon = '';
  @property({ type: String }) iconBorderColor = '';
  @property({ type: Number }) badgeValue = 0;
  @property({ type: String }) iconColor = '';
  @property({ type: String }) label = '';
  @property({ type: String }) url = '';
  @property({ type: String }) badgeColor = '';
  @property({ type: Boolean }) emitRouteEvent = false;

  static override get styles() {
    return [
      variables,
      svgWithJs,
      css`
        :host {
          display: flex;
          align-items: center;
          width: 100%;
          font-family: var(--font-family-sans-serif);
          font-size: 14px;
        }
        .link-container {
          position: relative;
          margin-top: 2px;
          white-space: nowrap;
        }
        a {
          color: var(--gray-900);
        }
        .icon-container {
          background-color: #ddd;
          width: 60px;
          height: 60px;
          border-radius: 50%;
          margin: 5px auto;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .top-badge {
          position: absolute;
          top: -5px;
          right: -15px;
          padding: 0px 14px 0px;
          line-height: 20px;
          border-radius: 10px;
          color: var(--white);
        }
        svg {
          width: 40px;
          height: 40px !important;
        }
        .left-badge {
          display: inline-block;
          align-self: center;
          padding: 0px 14px 0px;
          margin-right: 10px;
          border-radius: 10px;
          color: var(--white);
          min-width: 15px;
          text-align: center;
        }

        .link {
          cursor: pointer;
          text-decoration: underline;
        }
      `,
    ];
  }

  override render() {
    if (this.icon) {
      return html`<span>
        ${this.getIconTemplate()} ${this.getLinkTemplate()}
      </span>`;
    } else {
      return html`<span style="display:flex">
        ${this.getBadgeTemplate('left-badge')} ${this.getLinkTemplate()}
      </span>`;
    }
  }

  private handleClick(url: string) {
    this.dispatchEvent(
      new CustomEvent('navigate', {
        detail: { url },
      }),
    );
  }

  getIconTemplate() {
    return html`<div
      class="icon-container"
      style="background-color: var(--${this
        .color}); border: 1px solid var(--${this.iconBorderColor})"
    >
      ${this.getBadgeTemplate('top-badge')}
      <i
        class="${this.icon}"
        data-fa-transform="shrink-8"
        style="color: var(--${this.iconColor})"
      ></i>
    </div>`;
  }

  getBadgeTemplate(cssClass: string) {
    if (cssClass === 'top-badge' && !this.badgeValue) {
      return '';
    } else {
      return html`<span
        style="background-color: var(--${this.badgeColor})"
        class="${cssClass}"
        >${this.badgeValue}</span
      >`;
    }
  }

  getLinkTemplate() {
    if (this.emitRouteEvent) {
      return html`<div class="link-container">
        <span class="link" @click=${() => this.handleClick(this.url)}
          >${this.label}</span
        >
      </div>`;
    }
    return html`<div class="link-container">
      <a href=${this.url}>${this.label}</a>
    </div>`;
  }

  protected override firstUpdated(): void {
    library.add(
      faTools,
      faCalendarClock,
      faHourglassStart,
      faHourglassHalf,
      faHourglassEnd,
    );

    // @ts-ignore The dom.watch() TS definition indicates no parameters for this. However, when
    // you actually dig into the code, you see that it looks at the arguments after it gets into
    // the code and tries to pull some out if there aren't any (it even supplies an empty object
    // for options if you don't supply one yourself).
    //
    // Note: We have to wait for the first update for the shadow DOM to even be created.
    dom.watch({
      autoReplaceSvgRoot: this.shadowRoot,
      observeMutationsRoot: this.shadowRoot,
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'apt-labeled-data-point': LabeledDataPoint;
  }
}
